import { useState, useEffect, useCallback } from 'react';
import { palette, transition } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { buildRRule } from '../../utils/recurrence';
import { SOURCES, LABEL, INPUT } from './config';
import { MoneyInput } from '../../components/common/MoneyInput';
import { ClientPicker, type ClientSelection } from '../../components/common/ClientPicker';
import { DatePicker } from '../../components/common/DatePicker';
import { TimePicker } from '../../components/common/TimePicker';
import { MultiDatePicker } from '../../components/common/MultiDatePicker';
import type { Service } from '../../types';

interface TeamUser { id: string; first_name: string; last_name: string; }

type DateMode = 'single' | 'recurring' | 'multi';
const REPEAT_OPTIONS = [
  { value: 'weekly', label: 'Every week' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Every month' },
  { value: 'daily', label: 'Every day' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: Record<string, unknown>, userId: string) => Promise<boolean>;
  onCreateRecurring?: (data: Record<string, unknown>, rrule: string, userId: string) => Promise<void>;
  onCreateBatch?: (data: Record<string, unknown>, dates: string[], userId: string) => Promise<void>;
  services: Service[];
  users: TeamUser[];
  userId: string;
  initialDate?: string;
  initialTime?: string;
  initialAssigned?: string;
  initialSource?: string;
}

function addMinutes(time: string, mins: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + mins;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function CreateBookingPanel({ isOpen, onClose, onCreate, onCreateRecurring, onCreateBatch, services, users, userId, initialDate, initialTime, initialAssigned, initialSource }: Props) {
  const [clientId, setClientId] = useState<string | null>(null);
  // Rule #33: full ClientSelection captured; bookings table persists
  // client_id + denormalized client_name/client_phone synced from selection.
  const [clientSelection, setClientSelection] = useState<ClientSelection | null>(null);
  const [serviceId, setServiceId] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [assigned, setAssigned] = useState('');
  const [payStatus, setPayStatus] = useState('unpaid');
  const [amount, setAmount] = useState('');
  const [tip, setTip] = useState('');
  const [source, setSource] = useState('admin_panel');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Date mode
  const [dateMode, setDateMode] = useState<DateMode>('single');
  const [repeatPattern, setRepeatPattern] = useState('weekly');
  const [multiDates, setMultiDates] = useState<string[]>([]);

  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isOpen) {
      setClientId(null); setClientSelection(null); setServiceId('');
      setDate(initialDate ?? ''); setStartTime(initialTime ?? '09:00');
      setEndTime(initialTime ? addMinutes(initialTime, 60) : '10:00');
      setAssigned(initialAssigned ?? ''); setPayStatus('unpaid');
      setAmount(''); setTip(''); setSource(initialSource ?? 'admin_panel');
      setNotes(''); setError(null);
      setDateMode('single'); setRepeatPattern('weekly'); setMultiDates([]);
    }
  }, [isOpen]);
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */

  const handleServiceChange = (id: string) => {
    setServiceId(id);
    const svc = services.find(s => s.id === id);
    if (svc) {
      if (svc.duration_minutes) setEndTime(addMinutes(startTime, svc.duration_minutes));
      if (svc.price) setAmount(String(svc.price));
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!clientId) { setError('Client is required'); return; }
    if (dateMode !== 'multi' && !date) { setError('Date is required'); return; }
    if (dateMode === 'multi' && multiDates.length === 0) { setError('Select at least one date'); return; }
    setSaving(true); setError(null);
    const svc = services.find(s => s.id === serviceId);
    const baseData = {
      client_id: clientId,
      client_name: clientSelection?.name ?? null,
      client_phone: clientSelection?.phone ?? null,
      service_id: serviceId || null, service_name: svc?.name,
      date, start_time: startTime, end_time: endTime,
      assigned_id: assigned || null, status: 'confirmed',
      // Q1.5 boundary: hook accepts numbers; amount is canonical after MoneyInput blur.
      payment_status: payStatus, payment_amount: amount ? Number(amount) : null,
      tip_amount: tip ? Number(tip) : null, source, notes: notes || null,
      booked_price: svc?.price ?? null, price_type: 'standard',
    };
    const ok = await onCreate(baseData, userId);

    if (ok) {
      if (dateMode === 'recurring' && date && onCreateRecurring) {
        const rrule = buildRRule(repeatPattern, date);
        await onCreateRecurring({ ...baseData, title: svc?.name ?? 'Booking' }, rrule, userId);
      } else if (dateMode === 'multi' && multiDates.length > 0 && onCreateBatch) {
        await onCreateBatch({ ...baseData, title: svc?.name ?? 'Booking' }, multiDates, userId);
      }
      onClose();
    } else {
      setError('Failed to create booking');
    }
    setSaving(false);
  }, [clientId, clientSelection, serviceId, date, startTime, endTime, assigned, payStatus, amount, tip, source, notes, userId, onCreate, onCreateRecurring, onCreateBatch, onClose, dateMode, repeatPattern, multiDates, services]);

  const F: React.CSSProperties = { marginBottom: 14 };
  const MODE_BTN = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
    fontSize: 11.5, fontWeight: 500, fontFamily: 'inherit',
    background: active ? palette.g800 : 'transparent',
    color: active ? palette.white : palette.g500,
    transition: `all ${transition.fast}`,
  });

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title="New Booking">
      {error && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{error}</div>}
      <div style={F}><label style={LABEL}>Client *</label>
        <ClientPicker
          value={clientId}
          onChange={(sel) => { setClientId(sel?.clientId ?? null); setClientSelection(sel); }}
        />
      </div>
      {services.length > 0 && (
        <div style={F}><label style={LABEL}>Service</label>
          <select style={INPUT} value={serviceId} onChange={e => handleServiceChange(e.target.value)}>
            <option value="">Select service...</option>
            {services.map(s => <option key={s.id} value={s.id}>{s.name}{s.duration_minutes ? ` (${s.duration_minutes}min)` : ''}{s.price ? ` — $${s.price}` : ''}</option>)}
          </select>
        </div>
      )}

      {/* Date mode selector */}
      <div style={{ ...F, marginBottom: 8 }}>
        <label style={LABEL}>Schedule</label>
        <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 8, padding: 2, width: 'fit-content' }}>
          <button onClick={() => setDateMode('single')} style={MODE_BTN(dateMode === 'single')}>Single date</button>
          <button onClick={() => setDateMode('recurring')} style={MODE_BTN(dateMode === 'recurring')}>Recurring</button>
          <button onClick={() => setDateMode('multi')} style={MODE_BTN(dateMode === 'multi')}>Multiple dates</button>
        </div>
      </div>

      <div style={F}>
        <label style={LABEL}>{dateMode === 'multi' ? 'Dates *' : dateMode === 'recurring' ? 'Start date *' : 'Date *'}</label>
        {dateMode === 'multi' ? (
          <MultiDatePicker value={multiDates} onChange={setMultiDates} minDate={new Date().toISOString().split('T')[0]} />
        ) : (
          <DatePicker value={date || null} onChange={(v) => setDate(v ?? '')} minDate={new Date().toISOString().split('T')[0]} />
        )}
      </div>

      {dateMode === 'recurring' && (
        <div style={F}>
          <label style={LABEL}>Repeat</label>
          <select style={INPUT} value={repeatPattern} onChange={e => setRepeatPattern(e.target.value)}>
            {REPEAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Start</label>
          <TimePicker value={startTime || null} onChange={(v) => setStartTime(v ?? '')} step={15} />
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>End</label>
          <TimePicker value={endTime || null} onChange={(v) => setEndTime(v ?? '')} step={15} minTime={startTime || undefined} />
        </div>
      </div>
      <div style={F}><label style={LABEL}>Assigned to</label>
        <select style={INPUT} value={assigned} onChange={e => setAssigned(e.target.value)}>
          <option value="">Unassigned</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Amount</label><MoneyInput value={amount} onChange={setAmount} /></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Source</label>
          <select style={INPUT} value={source} onChange={e => setSource(e.target.value)}>
            {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>
      <div style={F}><label style={LABEL}>Notes</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={handleSubmit} disabled={saving} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{saving ? 'Creating...' : 'Create Booking'}</button>
      </div>
    </SlidePanel>
  );
}
